import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Redirect, Route, Switch } from 'react-router'
import ProductLessons from 'components/ProductLessons'

import { CreateClassModals } from './CreateClassModals'
import { LessonBreadcrumbs } from './LessonBreadcrumbs'
import { Wrapper } from './component.styles'
import { useAutoOpenTrigger } from './hooks'
import { getLessonPageViewModel } from './data'

const LessonsComponent = props => {
  const {
    classes = [],
    contentfulCourses,
    courseLinks,
    currentSiteV4,
    goToDashboard,
    lessonCompletionGoalValue,
    match,
    onClassClick,
    onComponentClick,
    onCourseClick,
    productName,
    productTabTitle,
    showSmallGroupCreation = true,
    // CreateCourse props
    lms,
    pathName,
    userAccess,
    userContext,
    userProfile,
    createCourseInstance,
    createCourseInstanceV2,
    createCourseInstanceV2ProgramFlex,
    deletingCourseCreatedStatus,
    deleteCourseInstance,
    updateCourseInstance,
  } = props || {}

  const createCourseTrigger = useAutoOpenTrigger()
  const createSmallGroupTrigger = useAutoOpenTrigger()
  const { baseUrl, productTitle, tabTitle, withVerb } = useMemo(
    () => getLessonPageViewModel({ match, productName, productTabTitle }),
    [match, productName, productTabTitle],
  )

  useEffect(() => {
    document.title = tabTitle
  }, [tabTitle])

  return (
    <Wrapper>
      <Switch>
        {/* Previewing or teaching a with nothing specified; go to just /lessons */}
        <Redirect exact from={`${withVerb}`} to={`${baseUrl}`} />
        {/* Show courses to preview and classes to teach (if there are any) */}
        <Route
          path={baseUrl}
          render={() => (
            <>
              <LessonBreadcrumbs goToDashboard={goToDashboard} />

              <ProductLessons
                classes={classes}
                contentfulCourses={contentfulCourses}
                courseLinks={courseLinks}
                currentSite={currentSiteV4}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                onCourseClick={onCourseClick}
                onCreateClass={createCourseTrigger.open}
                onCreateSmallGroup={
                  showSmallGroupCreation
                    ? createSmallGroupTrigger.open
                    : undefined
                }
                program={productName}
                showSmallGroupCreation={showSmallGroupCreation}
                title={productTitle}
              />
              <CreateClassModals
                contentfulCourses={contentfulCourses}
                createCourseAutoOpen={createCourseTrigger.autoOpen}
                createCourseInstance={createCourseInstance}
                createCourseInstanceV2={createCourseInstanceV2}
                createCourseInstanceV2ProgramFlex={
                  createCourseInstanceV2ProgramFlex
                }
                createSmallGroupAutoOpen={createSmallGroupTrigger.autoOpen}
                deleteCourseInstance={deleteCourseInstance}
                deletingCourseCreatedStatus={deletingCourseCreatedStatus}
                lms={lms}
                pathName={pathName}
                showSmallGroupCreation={showSmallGroupCreation}
                updateCourseInstance={updateCourseInstance}
                userAccess={userAccess}
                userContext={userContext}
                userProfile={userProfile}
              />
            </>
          )}
        />
      </Switch>
    </Wrapper>
  )
}

LessonsComponent.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoId: PropTypes.number,
      totalLessons: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  contentfulCourses: PropTypes.arrayOf(
    PropTypes.shape({
      displayTitle: PropTypes.string,
    }),
  ).isRequired,
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ).isRequired,
  createCourseInstance: PropTypes.func,
  createCourseInstanceV2: PropTypes.func,
  createCourseInstanceV2ProgramFlex: PropTypes.func,
  currentSiteV4: PropTypes.object,
  deleteCourseInstance: PropTypes.func,
  deletingCourseCreatedStatus: PropTypes.func,
  goToDashboard: PropTypes.func.isRequired,
  lms: PropTypes.object,
  match: PropTypes.shape({
    url: PropTypes.string,
    path: PropTypes.string,
    params: PropTypes.shape({
      productName: PropTypes.string,
    }),
  }).isRequired,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  pathName: PropTypes.string,
  productName: PropTypes.string,
  productTabTitle: PropTypes.string,
  showSmallGroupCreation: PropTypes.bool,
  updateCourseInstance: PropTypes.func,
  userAccess: PropTypes.object,
  userContext: PropTypes.object,
  userProfile: PropTypes.object,
}

export default LessonsComponent
